import { useState } from "react";
import { ExternalLink, Info, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EmployeeFlexiReimbursementPanel from "../../../../Employee/payroll/EmployeeFlexiReimbursementPanel";
import {
  PAYROLL_REIMB_CLAIM_LABEL,
  PAYROLL_REIMB_OPTIN_LABEL,
} from "../../../../utils/flexiReimbursementDesk";

const INFO_BY_MODE = {
  declare: {
    title: "About Opt-in",
    body: [
      "Opt-in sets how much of this reimbursement you want for the current financial year (Apr–Mar), up to your eligible limit.",
      "Maximum eligible / month comes from your salary structure. You cannot enter more than the FY maximum shown.",
      "After opt-in, raise claims with bills from Reimbursement Claim when the claim window is open.",
      "You may change or revoke an opt-in only while the company window and policy allow it (for example cool-down or max changes).",
      "Track balances and history under Employee → Payroll → Reimbursement.",
    ],
  },
  claim: {
    title: "About Claim",
    body: [
      "Submit a claim against an active opt-in for the selected component.",
      "Attach supporting bills where required. Approved amounts are paid as per company reimbursement rules.",
      "Track status and ledger under Employee → Payroll → Reimbursement.",
    ],
  },
};

export default function FlexiReimbursementRequestModal({
  isOpen,
  mode = "declare",
  employeeId,
  context,
  onClose,
  onSubmitted,
}) {
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const viewMode = mode === "claim" ? "claim" : "declare";
  const title =
    viewMode === "claim" ? PAYROLL_REIMB_CLAIM_LABEL : PAYROLL_REIMB_OPTIN_LABEL;
  const info = INFO_BY_MODE[viewMode];

  if (!isOpen) return null;

  const numericEmployeeId =
    employeeId != null && employeeId !== "" ? Number(employeeId) : null;

  const openPayrollHub = () => {
    onClose?.();
    navigate("/employee/payroll?tab=reimbursement");
  };

  return (
    <div className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/50 p-4 pt-20 sm:pt-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="ds-h4 truncate text-[hsl(var(--ds-fg))]">{title}</h3>
              <button
                type="button"
                onClick={() => setInfoOpen((v) => !v)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[hsl(var(--ds-primary))] transition hover:bg-[hsl(var(--ds-muted))]"
                aria-label="About reimbursement requests"
                aria-expanded={infoOpen}
              >
                <Info className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              {context?.employeeCode
                ? `${context.employeeCode} — ${context?.userName || "Employee"}`
                : context?.userName
                  ? context.userName
                  : "Current user"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {infoOpen ? (
          <div className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] px-5 py-3 text-xs leading-relaxed text-[hsl(var(--ds-fg))]">
            <p className="mb-2 font-semibold">{info.title}</p>
            <ul className="list-disc space-y-1.5 pl-4 text-[hsl(var(--ds-muted-fg))]">
              {info.body.map((line) => (
                <li key={line.slice(0, 40)}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!Number.isFinite(numericEmployeeId) ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              Link your login to an employee record to raise reimbursement requests.
            </p>
          ) : (
            <EmployeeFlexiReimbursementPanel
              employeeId={numericEmployeeId}
              mode={viewMode}
              hideHeader
              onActionSuccess={onSubmitted}
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <button
            type="button"
            onClick={openPayrollHub}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline"
          >
            Ledger, balances & history
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-[hsl(var(--ds-border))] px-4 py-2 text-xs font-medium text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
